'use client'

import { ComponentProps } from 'react'
import styled from 'styled-components'

import { timingFast, timingSlower } from '@syconium/little-miss-figgy/dist/constants/timing'

import { NextDialog, NextDialogBackdrop, NextDialogDismiss } from './NextDialog.client'

type NextDrawerDialogBackdropProps = ComponentProps<typeof NextDialogBackdrop>

const NextDrawerDialogBackdrop = styled((props: NextDrawerDialogBackdropProps) => {
  return <NextDialogBackdrop {...props} />
})`
  padding: 0px;
  overflow: hidden;
  display: block;

  transition:
    background-color ${timingSlower} ease,
    backdrop-filter ${timingSlower} ease;
  background-color: rgba(0, 0, 0, 0);
  backdrop-filter: blur(0px);
  &[data-enter] {
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(1px);
  }
`

type NextDrawerDialogBottomBackdropProps = ComponentProps<typeof NextDialogBackdrop>

const NextDrawerDialogBottomBackdrop = styled((props: NextDrawerDialogBottomBackdropProps) => {
  return <NextDialogBackdrop {...props} />
})`
  padding-bottom: 0px;
  padding-left: 0px;
  padding-right: 0px;
  align-items: unset;
  display: flex;
`

export const NextDrawerDialogFooter = styled.footer`
  border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  padding: ${o => o.theme.dimensions.layout.sidePadding.md}px;
  background-color: ${o => o.theme.color.fill.background};
  color: ${o => o.theme.color.text.primary.on.background};
`

const NextDrawerDialogDismiss = styled(NextDialogDismiss)<{ $pinTo: 'top-left' | 'top-right' }>`
  position: fixed;
  top: ${o => o.theme.dimensions.layout.sidePadding.md}px;
  ${o =>
    o.$pinTo === 'top-left'
      ? `
      left: ${o.theme.dimensions.layout.sidePadding.md}px;
    `
      : `
      right: ${o.theme.dimensions.layout.sidePadding.md}px;
    `};
  z-index: 1;
`

type NextDrawerDialogProps = ComponentProps<typeof NextDialog> & {
  variant: 'left' | 'right' | 'bottom'
  closeText: string
}

export const NextDrawerDialog = styled(
  ({
    children,
    variant,
    closeText,
    nestDialogInBackdrop = variant === 'bottom',
    backdrop = variant === 'bottom' ? NextDrawerDialogBottomBackdrop : NextDrawerDialogBackdrop,
    ...props
  }: NextDrawerDialogProps) => {
    return (
      <NextDialog backdrop={backdrop} nestDialogInBackdrop={nestDialogInBackdrop} {...props}>
        <NextDrawerDialogDismiss
          $pinTo={variant === 'left' ? 'top-left' : 'top-right'}
          aria-label={closeText}
        />
        {children}
      </NextDialog>
    )
  }
)`
  display: block;
  box-sizing: border-box;
  border-radius: 0px;
  margin: 0px;
  text-align: unset;
  opacity: 1;

  ${o =>
    o.variant !== 'bottom'
      ? `
    position: fixed;
    top: 0px;
    ${o.variant === 'left' ? 'left: 0px' : 'right: 0px'};

    min-width: unset;
    max-width: min(100%, 600px);
    min-height: unset;
    max-height: unset;

    width: 100vw;
    width: 100dvw;
    height: 100vh;
    height: min(100dvh, var(--dialog-viewport-height));

    overflow: auto;
    overscroll-behavior: contain;

    transition: transform ${timingSlower} ease;
    transform: ${o.variant === 'left' ? 'translateX(-100%)' : 'translateX(100%)'};
    &[data-enter] {
      transform: translateX(0%);
    }
    `
      : `
    min-width: 100%;
    max-width: 100%;
    min-height: 1px;
    max-height: unset;

    width: 100vw;
    width: 100dvw;
    height: unset;

    margin-top: auto;

    flex-grow: 0;
    flex-shrink: 0;

    transition: transform ${timingFast} ease;
    transform: translateY(50px) scale(1);
    &[data-enter] {
      transform: translateY(0px) scale(1);
    }
    `};
`
